import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useNotifications } from '@features/notifications/hooks/useNotifications'
import type { NotificationWire } from '@features/notifications/types'
import { useOutsideClick } from '@hooks/useOutsideClick'
import { Icon } from '@/icons/Icon'
import { useTranslation, type UseTranslation } from '@/i18n'

type TranslateFn = UseTranslation['t']

const KNOWN_NOTIF_TYPES = new Set<string>([
  'auth.first_login',
  'auth.new_device_login',
  'account.email_verified',
  'emergency.invite_received',
  'emergency.unlock_requested',
  'emergency.taken_over',
])

const notifTitleKey = (type: string): string => {
  if (type === 'auth.first_login') return 'common.ui.notifications_type_first_login'
  if (type === 'auth.new_device_login') return 'common.ui.notifications_type_new_device_login'
  if (type === 'account.email_verified') return 'common.ui.notifications_type_email_verified'
  if (type === 'emergency.invite_received')
    return 'common.ui.notifications_type_emergency_invite_received'
  if (type === 'emergency.unlock_requested')
    return 'common.ui.notifications_type_emergency_unlock_requested'
  if (type === 'emergency.taken_over') return 'common.ui.notifications_type_emergency_taken_over'

  return 'common.ui.notifications_type_generic'
}

const relativeTime = (t: TranslateFn, iso: string): string => {
  const created = Date.parse(iso)
  if (Number.isNaN(created)) return ''
  const diffSec = Math.max(0, Math.round((Date.now() - created) / 1000))
  if (diffSec < 60) return t('common.ui.notifications_relative_just_now')
  const min = Math.floor(diffSec / 60)
  if (min < 60) return t('common.ui.notifications_relative_minutes', { n: min })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t('common.ui.notifications_relative_hours', { n: hr })
  const day = Math.floor(hr / 24)

  return t('common.ui.notifications_relative_days', { n: day })
}

const notifMetaLine = (t: TranslateFn, item: NotificationWire): string | null => {
  if (item.type !== 'auth.new_device_login') return null
  const meta = item.meta ?? {}
  const ua = typeof meta.userAgent === 'string' && meta.userAgent ? meta.userAgent : null
  const ip = typeof meta.ipPrefix === 'string' && meta.ipPrefix ? meta.ipPrefix : null
  if (!ua && !ip) return t('common.ui.notifications_device_unknown')

  return t('common.ui.notifications_device_meta', {
    ua: ua ?? t('common.ui.notifications_device_unknown'),
    ip: ip ?? '—',
  })
}

/**
 * トップバー。design/js/components.jsx:107-121 を移植。
 *
 * crumbs prop は `crumbsFor(route)` の戻り値 (i18n key 配列、動的 ID は素のまま) を
 * 受け取る想定。Topbar 側で `t(key)` を当てて表示用文字列に解決する。
 * key として登録が無い文字列 (動的 ID) は i18n fallback で生のまま表示される。
 *
 *   - 検索: `searchQuery` で controlled、`onSearchChange` で入力ごと / Enter で確定通知
 *     (T-E1 配線、Vault / Send list 等の親側で text フィルタに反映)
 *   - 通知センター (T-F1 / T-H1): bell button で popover を開閉。useNotifications で
 *     `/api/notifications` を fetch し、未読件数を bell バッジに表示する。popover を
 *     開いた瞬間に既読化 (markAllRead) する。
 */
export interface TopbarProps {
  /** i18n key の配列。動的セグメント (vault item id 等) は key ではなく素の文字列を含めてよい。 */
  crumbs: readonly string[]
  onToggleNav?: () => void
  /** Controlled な検索 query (空文字 = 検索なし)。未指定なら入力 box は disabled */
  searchQuery?: string
  /** onChange の各 keystroke + Enter キー押下時の確定通知を 1 本にまとめる */
  onSearchChange?: (query: string) => void
}

export const Topbar = ({
  crumbs,
  onToggleNav,
  searchQuery,
  onSearchChange,
}: TopbarProps): JSX.Element => {
  const { t } = useTranslation()
  const isSearchEnabled = typeof onSearchChange === 'function'
  const value = searchQuery ?? ''
  const [isNotifOpen, setIsNotifOpen] = useState(false)
  const notifRef = useRef<HTMLDivElement | null>(null)

  const {
    items: notifItems,
    unreadCount,
    refresh: refreshNotifs,
    markAllRead,
    remove: removeNotif,
    clearAll: clearAllNotifs,
  } = useNotifications()

  // T-F1: 外クリックで notification popover を閉じる
  useOutsideClick(notifRef, isNotifOpen, () => setIsNotifOpen(false))

  // popover を開いたら最新を取得し、未読を全て既読にする
  useEffect(() => {
    if (!isNotifOpen) return
    void refreshNotifs()
    if (unreadCount > 0) {
      void markAllRead()
    }
    // 未読件数の依存を入れると open 中に再発火するので開閉のみで発火
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotifOpen])

  const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
    // Enter で確定通知 (現状の onChange と同じ値だが、IME 確定タイミングを尊重)
    if (ev.key === 'Enter' && !ev.nativeEvent.isComposing) {
      ev.preventDefault()
      onSearchChange?.(value)
    }
  }

  return (
    <header className="topbar" data-testid="topbar">
      <button
        type="button"
        className="icon-btn"
        onClick={onToggleNav}
        title={t('common.ui.sidebar')}
        aria-label={t('common.ui.sidebar')}
      >
        <Icon name="panel-left" />
      </button>
      <div className="crumbs">
        {crumbs.map((seg, i) => (
          <Fragment key={`${i}-${seg}`}>
            {i > 0 && <span className="sep">/</span>}
            <span className={i === crumbs.length - 1 ? 'here' : ''}>{t(seg)}</span>
          </Fragment>
        ))}
      </div>
      <div className="spacer" />
      {/* C-1: 検索 box は filter 配線がある route (現状 vault 系のみ) でのみ
       *  rendering する。`onSearchChange` 未指定 = 親が検索を扱わないルート
       *  と解釈し、box ごと非表示にする (旧実装は disabled で残置していたが、
       *  全 route で見えるため「入力しても効かない」UX の嘘になっていた)。 */}
      {isSearchEnabled && (
        <div className="search" data-testid="topbar-search">
          <span className="ico">
            <Icon name="search" />
          </span>
          <input
            type="text"
            placeholder={t('common.ui.search_vault_placeholder')}
            aria-label={t('common.ui.search_vault_label')}
            value={value}
            onChange={ev => onSearchChange?.(ev.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}
      <div className="topbar-notif" ref={notifRef}>
        <button
          type="button"
          className="icon-btn topbar-notif-btn"
          onClick={() => setIsNotifOpen(prev => !prev)}
          aria-haspopup="dialog"
          aria-expanded={isNotifOpen}
          title={t('common.ui.notifications')}
          aria-label={t('common.ui.notifications')}
          data-testid="topbar-notif-button"
        >
          <Icon name="bell" />
          {unreadCount > 0 && (
            <span className="topbar-notif-badge" aria-hidden="true">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        {isNotifOpen && (
          <div
            className="notif-popover"
            role="dialog"
            aria-label={t('common.ui.notifications')}
            data-testid="topbar-notif-popover"
          >
            <div className="notif-popover-head">
              <span>{t('common.ui.notifications')}</span>
              {notifItems.length > 0 && (
                <button
                  type="button"
                  className="notif-clear-all"
                  onClick={() => void clearAllNotifs()}
                  data-testid="notif-clear-all"
                >
                  {t('common.ui.notifications_clear_all')}
                </button>
              )}
            </div>
            {notifItems.length === 0 ? (
              <p className="notif-popover-empty">{t('common.ui.notifications_empty')}</p>
            ) : (
              <ul className="notif-list" role="list">
                {notifItems.map(item => {
                  const titleKey = notifTitleKey(item.type)
                  const isUnread = item.readAt === null
                  const isKnown = KNOWN_NOTIF_TYPES.has(item.type)
                  const fallback = isKnown ? null : item.type
                  const metaLine = notifMetaLine(t, item)

                  return (
                    <li
                      key={item.id}
                      className={`notif-row${isUnread ? ' is-unread' : ''}`}
                      data-testid={`notif-row-${item.id}`}
                    >
                      <div className="notif-row-body">
                        <div className="notif-row-title">{t(titleKey)}</div>
                        {fallback && <div className="notif-row-sub">{fallback}</div>}
                        {metaLine && <div className="notif-row-sub">{metaLine}</div>}
                        <div className="notif-row-time">{relativeTime(t, item.createdAt)}</div>
                      </div>
                      <button
                        type="button"
                        className="notif-row-delete icon-btn sm"
                        title={t('common.ui.notifications_delete')}
                        aria-label={t('common.ui.notifications_delete')}
                        onClick={() => void removeNotif(item.id)}
                        data-testid={`notif-delete-${item.id}`}
                      >
                        <Icon name="trash" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
